import { Badge, Button } from "@procurement-agents/ui";
import { ApiError, type AgentDiff } from "@procurement-agents/scope-api";
import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useScopeApi } from "../api";
import { wordDiff } from "../diff";
import { DraftGate, useAgents } from "./AgentsApp";
import { AGENT_LABEL } from "./labels";

/** The diff against the published version across settings, overlays and profile; the change note; the checklist. */
export function Publish() {
  const { api, toast } = useScopeApi();
  const { overview, draft, refresh } = useAgents();
  const nav = useNavigate();
  const [diff, setDiff] = useState<AgentDiff | null>(null);
  const [note, setNote] = useState(draft?.change_note ?? "");
  const [busy, setBusy] = useState(false);
  useEffect(() => { if (draft) api.agentDiff(overview.published.version, draft.version).then(setDiff).catch(() => setDiff(null)); }, [api, draft, overview.published.version]);
  if (!draft) return <><DraftGate /><p className="muted small">Nothing to publish without a draft.</p></>;
  const errors = draft.errors ?? [];
  const overlays = Object.entries(draft.overlays).filter(([, o]) => o.text.trim() || o.examples.length);
  const linted = overlays.every(([, o]) => o.lint && !o.lint.items.some((i) => i.severity === "blocking")) && !errors.some((e) => e.includes("not linted") || e.includes("lint reports"));
  const validation = errors.filter((e) => !e.startsWith("no preview") && !e.includes("lint"));
  const ok = errors.length === 0 && note.trim().length > 0;
  const publish = async () => {
    setBusy(true);
    try { const v = await api.publishAgents(note); toast(`Published agent configuration v${v.version}. New runs use it; runs in flight keep theirs.`); await refresh(); nav("../history", { relative: "path" }); }
    catch (e) { toast(e instanceof ApiError ? e.detail : String(e), "warn"); }
    finally { setBusy(false); }
  };
  const discard = async () => { if (!window.confirm("Discard this draft? Its previews are removed by the daily sweep.")) return; await api.discardAgentDraft(); toast("Draft discarded."); await refresh(); nav("../pipeline", { relative: "path" }); };
  return (
    <section className="publish-page">
      <h3>Changes against v{overview.published.version}</h3>
      {diff ? <DiffView diff={diff} /> : <p className="muted small">Loading the diff…</p>}
      <h3>Checklist</h3>
      <ul className="checklist">
        <li><Badge tone={validation.length ? "bad" : "good"}>{validation.length ? "fails" : "passed"}</Badge> Validation{validation.length > 0 && <ul>{validation.map((e, i) => <li key={i} className="small">{e}</li>)}</ul>}</li>
        <li><Badge tone={linted ? "good" : "bad"}>{linted ? "done" : "pending"}</Badge> Every tenant instruction linted, none blocking{overlays.length === 0 && <span className="muted small"> (no instructions written)</span>}</li>
        <li><Badge tone={draft.previewed ? "good" : "bad"}>{draft.previewed ? "done" : "pending"}</Badge> A preview has run for exactly this content</li>
      </ul>
      <label className="field"><span className="field-label">Change note (required)</span><textarea rows={3} aria-label="Change note" value={note} onChange={(e) => setNote(e.target.value)} /></label>
      <div className="row">
        <Button variant="primary" onClick={() => void publish()} disabled={!ok || busy}>Publish v{draft.version}</Button>
        <Button variant="quiet" onClick={() => void discard()} disabled={busy}>Discard draft</Button>
      </div>
    </section>
  );
}

const text = (v: unknown) => (v == null ? "—" : Array.isArray(v) ? v.join(", ") : String(v));

export function DiffView({ diff }: { diff: AgentDiff }) {
  const empty = !Object.keys(diff.settings).length && !Object.keys(diff.overlays).length && !Object.keys(diff.profile).length;
  if (empty) return <p className="muted">No differences.</p>;
  return (
    <div className="version-diff agent-diff">
      {Object.entries(diff.profile).map(([k, v]) => <p key={k} className="small"><strong>pipeline · {k}</strong>: <del>{text(v.before)}</del> → <ins>{text(v.after)}</ins></p>)}
      {Object.entries(diff.settings).map(([role, fields]) => <p key={role} className="small"><strong>{AGENT_LABEL[role as keyof typeof AGENT_LABEL]?.name ?? role}</strong>: {Object.entries(fields).map(([f, v]) => <span key={f} className="diff-field-inline">{f} <del>{text(v.before)}</del> → <ins>{text(v.after)}</ins> </span>)}</p>)}
      {Object.entries(diff.overlays).map(([role, o]) => (
        <details key={role} open className="diff-section">
          <summary><strong>{AGENT_LABEL[role as keyof typeof AGENT_LABEL]?.name ?? role} instructions</strong> <span className="muted small">examples {o.examples_before} → {o.examples_after}</span></summary>
          <pre className="diff">{wordDiff(o.before, o.after).map((x, i) => x.type === "same" ? <span key={i}>{x.text}</span> : x.type === "add" ? <ins key={i}>{x.text}</ins> : <del key={i}>{x.text}</del>)}</pre>
        </details>
      ))}
    </div>
  );
}
